import { createContext } from "react";
import type { AttributeDefinition } from "../../../workspace/shared";
import type { ServiceRefs } from "../service/service-refs";
import type { ActionOption } from "../use-workflow-actions";

export interface Option {
  id: string;
  name: string;
}
/** A reusable workflow another one can run; only a live one can be called when the caller is published. */
export interface ReusableOption extends Option {
  state: string;
}

/** What the step editors pick from: endpoints, teams, members, lead stages, agents and actions. */
export interface BuilderRefs {
  endpoints: Option[];
  teams: Option[];
  members: Option[];
  /** The members a new lead can be given to (they hold `leads.edit`); absent while their roles are unknown, then `members` is offered. */
  leadOwners?: Option[];
  stages: Option[];
  agents: Option[];
  actions: ActionOption[];
  attributes: AttributeDefinition[];
  slaPolicies: Option[];
  reusables: ReusableOption[];
  /** Ready websites with a public link (what a Show article step can name); absent where nothing loads them. */
  sources?: Option[];
  /** What the Service Desk steps pick from (ticket types, statuses, codes, SLA policies, fields); absent where the Service Desk cannot be used. */
  service?: ServiceRefs;
  /** Some of the lists above are still loading (an empty list may just not have arrived). */
  loading: boolean;
}

export const EMPTY_REFS: BuilderRefs = { endpoints: [], teams: [], members: [], stages: [], agents: [], actions: [], attributes: [], slaPolicies: [], reusables: [], loading: false };

/**
 * Kept in a module of its own, with nothing but `react` to import. The provider (`RefsProvider`) and the consumers
 * (`useRefs`) must share this one object: when the dev server serves `refs.tsx` under two URLs (a stale HMR
 * timestamp on some importers) each copy would otherwise create its own context, and every consumer in the other
 * copy would read `EMPTY_REFS` - empty endpoint, team, member and stage pickers although the API returned them.
 * A module with no imports is only ever invalidated by an edit of its own, so it stays a single instance.
 */
export const RefsContext = createContext<BuilderRefs>(EMPTY_REFS);
